import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useNotifications } from "./hooks/useNotifications";
import { useEffect } from "react";
import MobileNavigation from "@/components/MobileNavigation";
import DesktopNavigation from "@/components/DesktopNavigation";
import DesktopHeader from "@/components/DesktopHeader";
import Index from "./pages/Index";
import Map from "./pages/Map";
import NotFound from "./pages/NotFound";
import MentionsLegales from "./pages/MentionsLegales";
import Auth from "./pages/Auth";
import PlaceDetails from "./pages/PlaceDetails";
import Profile from "./pages/Profile";
import Tours from "./pages/Tours";
import TourDetails from "./pages/TourDetails";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import SitemapView from "./pages/SitemapView";


const queryClient = new QueryClient();

const AppContent = () => {
  // Initialize notifications on app start
  useNotifications();
  
  // Clean up old navigation state on app start
  useEffect(() => {
    const cleanupOldNavigationState = () => {
      const currentVersion = localStorage.getItem('build_timestamp');
      const keys = Object.keys(sessionStorage).filter(key => key.startsWith('nav_state_'));
      
      // Remove navigation states older than 24 hours
      const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
      
      keys.forEach(key => {
        try {
          const stateStr = sessionStorage.getItem(key);
          if (stateStr) {
            const state = JSON.parse(stateStr);
            
            // Remove if version doesn't match or if too old
            if (state.version !== currentVersion || state.timestamp < oneDayAgo) {
              sessionStorage.removeItem(key);
              console.log('🧹 Cleaned up old navigation state:', key);
            }
          }
        } catch (error) {
          // Invalid state, remove it
          sessionStorage.removeItem(key);
        }
      });
    };
    
    cleanupOldNavigationState();
  }, []);
  
  return (
    <>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/map" element={<Map />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/profil" element={<Profile />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/tours" element={<Tours />} />
          <Route path="/tours/:tourSlug" element={<TourDetails />} />
          <Route path="/place/:placeName" element={<PlaceDetails />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/sitemap" element={<SitemapView />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <NavigationManager />
      </BrowserRouter>
    </>
  );
};

// Component to conditionally render navigation based on route
const NavigationManager = () => {
  const location = useLocation();
  const isBlogPage = location.pathname.startsWith('/blog');
  
  if (isBlogPage) {
    return null;
  }
  
  return (
    <>
      <MobileNavigation />
      <DesktopNavigation />
      <DesktopHeader />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AppContent />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
